import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/AdminLayout';
import { ChevronDown, ChevronRight, Search, Calendar, RefreshCw, X, Activity as ActivityIcon } from 'lucide-react';
import ConfirmationModal from '@/components/ConfirmationModal';

const ActivityLog = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [projectActivities, setProjectActivities] = useState({});
  const [loadingActivities, setLoadingActivities] = useState({});
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 25;
  
  // Filters
  const [filters, setFilters] = useState({
    companyId: '',
    userId: '',
    action: '',
    startDate: '',
    endDate: '',
    search: ''
  });
  
  // Filter inputs
  const [searchInput, setSearchInput] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  
  // Companies and users for dropdown
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  // Confirmation modal
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: () => {},
    confirmText: 'OK',
    showCancel: false
  });

  useEffect(() => {
    fetchCompanies();
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [page, filters]);

  useEffect(() => {
    if (companySearch) {
      const filtered = companies.filter(c => 
        c.companyName.toLowerCase().includes(companySearch.toLowerCase())
      );
      setFilteredCompanies(filtered);
      setShowCompanyDropdown(true);
    } else {
      setFilteredCompanies([]);
      setShowCompanyDropdown(false);
    }
  }, [companySearch, companies]);

  useEffect(() => {
    if (userSearch) {
      const filtered = users.filter(u => 
        u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
        u.email?.toLowerCase().includes(userSearch.toLowerCase())
      );
      setFilteredUsers(filtered);
      setShowUserDropdown(true);
    } else {
      setFilteredUsers([]);
      setShowUserDropdown(false);
    }
  }, [userSearch, users]);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      
      if (filters.companyId) params.append('companyId', filters.companyId);
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.action) params.append('action', filters.action);
      if (filters.startDate) params.append('startDate', new Date(filters.startDate).toISOString());
      if (filters.endDate) params.append('endDate', new Date(filters.endDate).toISOString());
      if (filters.search) params.append('search', filters.search);

      const response = await axios.get(`${API}/admin/audit-logs/projects?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setProjects(response.data.projects || []);
      setTotalPages(response.data.totalPages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to load activity logs',
        type: 'danger',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectActivities = async (projectId) => {
    if (projectActivities[projectId]) {
      return; // Already loaded
    }

    setLoadingActivities(prev => ({ ...prev, [projectId]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/audit-logs/project/${projectId}/recent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjectActivities(prev => ({
        ...prev,
        [projectId]: response.data.activities || []
      }));
    } catch (error) {
      console.error('Error fetching project activities:', error);
    } finally {
      setLoadingActivities(prev => ({ ...prev, [projectId]: false }));
    }
  };

  const toggleProject = (projectId) => {
    const newExpanded = !expandedProjects[projectId];
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: newExpanded
    }));
    
    if (newExpanded) {
      fetchProjectActivities(projectId);
    }
  };

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, search: searchInput }));
    setPage(1);
  };

  const selectCompany = (company) => {
    setFilters(prev => ({ ...prev, companyId: company.id }));
    setCompanySearch(company.companyName);
    setShowCompanyDropdown(false);
    setPage(1);
  };

  const selectUser = (user) => {
    setFilters(prev => ({ ...prev, userId: user.id }));
    setUserSearch(user.name || user.email);
    setShowUserDropdown(false);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchInput('');
    setCompanySearch('');
    setUserSearch('');
    setFilters({
      companyId: '',
      userId: '',
      action: '',
      startDate: '',
      endDate: '',
      search: ''
    });
    setPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionBadgeClass = (action) => {
    switch (action) {
      case 'CREATE': return 'bg-green-100 text-green-800';
      case 'UPDATE': return 'bg-blue-100 text-blue-800';
      case 'DELETE': return 'bg-red-100 text-red-800';
      case 'SHARED': return 'bg-purple-100 text-purple-800';
      case 'UNSHARED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const hasActiveFilters = searchInput || filters.companyId || filters.userId || filters.action || filters.startDate || filters.endDate;

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h2 className="text-3xl font-bold mb-2" style={{ background: '#FF6600', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Activity Log
            </h2>
            <p className="text-gray-600">Track all project activities and changes made by users</p>
          </div>

          {/* Search and Filter Bar */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="space-y-4">
              {/* First Row */}
              <div className="flex gap-4">
                {/* Search */}
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      placeholder="Search by project name..."
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Company Filter */}
                <div className="w-64 relative">
                  <input
                    type="text"
                    value={companySearch}
                    onChange={(e) => setCompanySearch(e.target.value)}
                    placeholder="Filter by company..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  {showCompanyDropdown && filteredCompanies.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredCompanies.map((company) => (
                        <button
                          key={company.id}
                          type="button"
                          onClick={() => selectCompany(company)}
                          className="w-full text-left px-4 py-3 hover:bg-orange-50 transition-colors"
                        >
                          {company.companyName}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* User Filter */}
                <div className="w-64 relative">
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Filter by user..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  {showUserDropdown && filteredUsers.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredUsers.map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => selectUser(user)}
                          className="w-full text-left px-4 py-3 hover:bg-orange-50 transition-colors"
                        >
                          <div>{user.name || 'N/A'}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Second Row */}
              <div className="flex gap-4">
                {/* Action Filter */}
                <div className="w-48">
                  <select
                    value={filters.action}
                    onChange={(e) => setFilters(prev => ({ ...prev, action: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="">All Actions</option>
                    <option value="CREATE">Create</option>
                    <option value="UPDATE">Update</option>
                    <option value="DELETE">Delete</option>
                    <option value="SHARED">Shared</option>
                    <option value="UNSHARED">Unshared</option>
                  </select>
                </div>

                {/* Start Date */}
                <div className="w-48">
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                {/* End Date */}
                <div className="w-48">
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                {/* Search Button */}
                <button
                  onClick={handleSearch}
                  className="text-white px-6 py-3 rounded-lg hover:opacity-90 transition-all"
                  style={{ background: '#FF6600' }}
                >
                  Search
                </button>

                {/* Clear Filters Button */}
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Clear
                  </button>
                )}

                {/* Refresh Button */}
                <button
                  onClick={fetchProjects}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              Showing {projects.length} of {total} projects
            </p>
          </div>

          {/* Activity Table */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <div className="text-gray-500">Loading activity logs...</div>
              </div>
            ) : projects.length === 0 ? (
              <div className="p-12 text-center">
                <ActivityIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No activity logs found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Action</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {projects.map((project) => (
                      <React.Fragment key={project.projectId}>
                        <tr className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <button
                                onClick={() => toggleProject(project.projectId)}
                                className="mr-2 hover:text-orange-600"
                              >
                                {expandedProjects[project.projectId] ? (
                                  <ChevronDown className="w-5 h-5" />
                                ) : (
                                  <ChevronRight className="w-5 h-5" />
                                )}
                              </button>
                              <div className="text-sm font-medium text-gray-900">
                                {project.projectName || 'Unnamed Project'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{project.companyName || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActionBadgeClass(project.lastAction)}`}>
                              {project.lastAction}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{project.lastActionBy || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(project.lastActionDate)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {project.lastActivityDetails || '-'}
                          </td>
                        </tr>
                        
                        {/* Expanded Row */}
                        {expandedProjects[project.projectId] && (
                          <tr>
                            <td colSpan="6" className="px-6 py-4 bg-gray-50">
                              {loadingActivities[project.projectId] ? (
                                <div className="text-center text-gray-500 py-4">Loading activities...</div>
                              ) : (
                                <div className="space-y-2">
                                  <h4 className="font-semibold text-sm text-gray-700 mb-3">Recent Activities (Last 10)</h4>
                                  {projectActivities[project.projectId]?.length > 0 ? (
                                    <div className="space-y-2">
                                      {projectActivities[project.projectId].map((activity, idx) => (
                                        <div key={idx} className="bg-white p-3 rounded-lg border border-gray-200">
                                          <div className="flex items-center gap-3">
                                              <span style={{flexBasis:'72px'}} className={`inline-flex justify-center px-2 py-1 text-xs font-semibold rounded-full shrink-0 ${getActionBadgeClass(activity.action)}`}>
                                                {activity.action}
                                              </span>
                                              <span style={{flexBasis:'205px'}} className="text-sm font-medium text-gray-900 truncate shrink-0">{activity.userName && activity.userName !== 'Unknown' && activity.userName !== 'N/A' ? activity.userName : '-'}{activity.userRole && activity.userRole !== 'N/A' ? ` (${activity.userRole})` : ''}</span>
                                              <span style={{flexBasis:'18%'}} className="text-xs text-gray-600 truncate shrink-0">{activity.userEmail && activity.userEmail !== 'Unknown' && activity.userEmail !== 'N/A' ? activity.userEmail : '-'}</span>
                                              <span style={{flexBasis:'400px'}} className="text-xs text-gray-500 truncate shrink-0">{activity.companyName || '-'}</span>
                                              <span style={{flexBasis:'100px'}} className="text-xs text-gray-400 font-mono shrink-0 truncate">{activity.ipAddress && activity.ipAddress !== 'N/A' ? activity.ipAddress : '-'}</span>
                                              <span style={{width:'125px', maxWidth:'125px', fontSize:'11px'}} className="text-gray-500 shrink-0 text-right whitespace-nowrap overflow-hidden">
                                                {activity.timestamp ? formatDate(activity.timestamp) : '-'}
                                              </span>
                                          </div>
                                          {activity.details && (
                                            <div className="mt-2 text-xs text-gray-600">{activity.details}</div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="text-center text-gray-500 py-4">No activities found</div>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {!loading && projects.length > 0 && (
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Page {page} of {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white transition-colors"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
        showCancel={confirmModal.showCancel}
      />
    </AdminLayout>
  );
};

export default ActivityLog;
